import { focusableElements } from '@/helpers/focusUtils';
import React, { useEffect, useRef } from 'react';
import { useEvent } from '../../../hooks/useEvent';
/**
 * Hook to replace default carousel tabbing implementation provided
 * by the Swiper library.
 *
 * The original implementation has the following issues that apply
 * to our use case:
 *
 *  - We duplicate some slides to make sure there are enough for
 *    loop mode. Swiper is unaware so will tab through these duplicates
 *    which is not desired.
 *  - Tabbing while in loop mode doesn't work after slide 9 (Swiper bug).
 *
 * To use this hook, the Swiper carousel must have tabIndex="-1" set on all
 * but the active slide.
 *
 * @param ref
 * @param itemCount
 * @param isLoopMode
 */
export function useCarouselTabbing(ref, itemCount, isLoopMode) {
    const isUsingLeftRightArrows = useRef(false);
    const [swiperReady, setSwiperReady] = React.useState(false);
    const [activeTabbingIndex, setActiveTabbingIndex] = React.useState(0);
    useEffect(() => {
        if (!ref || !ref.current || !swiperReady) {
            return;
        }
        const handler = (swiper) => {
            setActiveTabbingIndex(swiper.realIndex);
        };
        ref?.current?.on('realIndexChange', handler);
        return () => {
            ref?.current?.off('realIndexChange', handler);
        };
    }, [swiperReady]);
    useEvent('keydown', (event) => {
        if (!ref || !ref.current || !swiperReady) {
            return;
        }
        if (!isLoopMode && event.key === 'Tab') {
            const slides = ref.current.slides;
            const activeSlide = slides[ref.current.activeIndex];
            if (activeSlide) {
                const focusableElementsInSlide = activeSlide.querySelectorAll(focusableElements.join(','));
                // If there are focusable elements in the current slide, let the browser handle tab navigation
                if (focusableElementsInSlide.length > 0) {
                    return; // Don't prevent default, allow normal tab behavior
                }
            }
        }
        if (!isLoopMode) {
            setActiveTabbingIndex((prev) => {
                const newIndex = event.key === 'ArrowLeft' ? Math.max(prev - 1, 0) : Math.min(prev + 1, itemCount - 1);
                return newIndex;
            });
        }
        if (event.key === 'ArrowLeft') {
            isUsingLeftRightArrows.current = true;
            ref.current?.slidePrev();
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        if (event.key === 'ArrowRight') {
            isUsingLeftRightArrows.current = true;
            ref.current?.slideNext();
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        isUsingLeftRightArrows.current = false;
    }, ref?.current?.el, !swiperReady);
    useEffect(() => {
        if (!isUsingLeftRightArrows.current) {
            return;
        }
        const slides = ref?.current?.slides ?? [];
        const activeTabbingIndex = slides.find((slide) => slide.querySelector(focusableElements.join(',')));
        const focusableElement = activeTabbingIndex?.querySelector(focusableElements.join(','));
        focusableElement?.focus();
    }, [activeTabbingIndex]);
    useEvent('mousemove', () => (isUsingLeftRightArrows.current = false));
    return { activeTabbingIndex, setSwiperReady: () => setSwiperReady(true) };
}
export function useCarouselImpressions(ref, cards, onSlideImpressions) {
    const [cardImpressions, setCardImpressions] = React.useState([]);
    useEffect(() => {
        const container = ref.current;
        if (!container || cards.length === 0) {
            return;
        }
        const onSlideChange = () => {
            const visibleCards = container.slides
                .filter((slide) => slide.classList.contains('swiper-slide-visible'))
                .map((slide) => cards.find((card) => slide.dataset.key === card.key))
                .filter(Boolean);
            setCardImpressions((previous) => {
                const newImpressions = visibleCards.filter((card) => !previous.some((prev) => prev.index === card.index));
                if (newImpressions.length === 0) {
                    return previous;
                }
                const updatedImpressions = [...previous, ...newImpressions];
                return updatedImpressions;
            });
        };
        container.on('slideChangeTransitionEnd', onSlideChange);
        onSlideChange();
        return () => container.off('slideChangeTransitionEnd', onSlideChange);
    }, [cards.length]);
    useEffect(() => {
        if (cardImpressions.length === 0) {
            return;
        }
        onSlideImpressions(cardImpressions.map(({ item, index }) => ({ item, index })));
    }, [cardImpressions]);
}
